/* eslint-disable react-hooks/exhaustive-deps */
import {
  ListItemIcon,
  ListItemText,
  MenuItem,
  type MenuItemProps,
  Tooltip,
} from '@mui/material';
import { type ExternalFile } from '@/lib/types';
import useGooglePicker from './useGooglePicker';
import GoogleDriveIcon from '../utils/icons/GoogleDriveIcon';
import ErrorIcon from '../utils/icons/ErrorIcon';
import { FormattedMessage } from 'react-intl';
import { useEffect, useState } from 'react';
import makeGoogleDriveExternalFilesPublic from './makeGoogleDriveExternalFilesPublic';
import IsMakingSelectedFilesPublicMessage from '../utils/IsMakingSelectedFilesPublicMessage';
import MenuItemLoadingIndicator from '@/components/Menu/MenuItemLoadingIndicator';
import { blockPageClose, unblockPageClose } from '@/lib/pageCloseBlocking';

type GoogleDriveFilePickerProps = Omit<
  MenuItemProps,
  'onClick' | 'disabled'
> & {
  onPickFiles: (pickedFiles: ExternalFile[]) => void;
  onLoadStart: () => string;
  onLoadEnd: (loaderId: string) => void;
  onRegisterError: () => string;
  onRemoveError: (errorId: string) => void;
};

const GoogleDriveFilePicker = ({
  onPickFiles,
  onLoadStart,
  onLoadEnd,
  onRegisterError,
  onRemoveError,
  ...props
}: GoogleDriveFilePickerProps) => {
  const [loading, hasError, showGooglePicker] = useGooglePicker();
  const [registeredErrorId, setRegisteredErrorId] = useState<string | null>(
    null,
  );

  const [isMakingSelectedFilesPublic, setIsMakingSelectedFilesPublic] =
    useState(false);

  const openGooglePicker = async (): Promise<void> => {
    const pageCloseBlockerId = blockPageClose();
    const loaderId = onLoadStart();

    const externalFiles = await showGooglePicker();

    if (externalFiles.length === 0) {
      onLoadEnd(loaderId);

      return;
    }

    setIsMakingSelectedFilesPublic(true);

    const publicExternalFiles =
      await makeGoogleDriveExternalFilesPublic(externalFiles);

    onPickFiles(publicExternalFiles);

    setIsMakingSelectedFilesPublic(false);

    onLoadEnd(loaderId);
    unblockPageClose(pageCloseBlockerId);
  };

  useEffect(() => {
    if (hasError && registeredErrorId === null) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRegisteredErrorId(onRegisterError());

      return;
    }

    if (!hasError && registeredErrorId !== null) {
      onRemoveError(registeredErrorId);

      setRegisteredErrorId(null);
    }
  }, [hasError]);

  return (
    <MenuItem
      onClick={openGooglePicker}
      disabled={loading || isMakingSelectedFilesPublic}
      {...props}
    >
      {isMakingSelectedFilesPublic ? (
        <IsMakingSelectedFilesPublicMessage />
      ) : (
        <>
          <ListItemIcon>
            {loading ? (
              <MenuItemLoadingIndicator />
            ) : hasError ? (
              <Tooltip
                title={
                  <FormattedMessage id="portfolio.challenge.files.add.select.google_drive.connection_error" />
                }
              >
                <ErrorIcon />
              </Tooltip>
            ) : (
              <GoogleDriveIcon />
            )}
          </ListItemIcon>
          <ListItemText>
            <FormattedMessage id="portfolio.challenge.files.add.menu.items.select_from_google_drive" />
          </ListItemText>
        </>
      )}
    </MenuItem>
  );
};

export default GoogleDriveFilePicker;
